import { useMemo, useState } from "react";
import { AppLayout } from "../../components/layout/Header";
import {
  exportSuggestions,
  listSuggestions,
  suggestionBreakdown,
  SUGGESTION_CATEGORIES,
  SUGGESTION_STATUSES,
  updateSuggestion,
} from "../../js/api/suggestions";
import { formatDate } from "../../js/utils/formatters";
import { toTitleCase } from "../../js/utils/titleCase";

const ROLES = ["", "buyer", "seller", "agent", "broker", "admin"];

function download(file) {
  const blob = new Blob([file.body], { type: file.mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function AdminSuggestions() {
  const [pulse, setPulse] = useState(0);
  const [role, setRole] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [notesDraft, setNotesDraft] = useState({});

  const all = useMemo(() => listSuggestions(), [pulse]);
  const rows = useMemo(
    () =>
      all.filter((item) => {
        if (role && item.from_role !== role) return false;
        if (category && item.category !== category) return false;
        if (status && (item.status || "new") !== status) return false;
        return true;
      }),
    [all, role, category, status]
  );
  const breakdown = useMemo(() => suggestionBreakdown(all), [all]);

  function saveNotes(id) {
    updateSuggestion(id, { admin_notes: notesDraft[id] ?? rows.find((item) => item.id === id)?.admin_notes });
    setPulse((n) => n + 1);
  }

  function setStatusFor(id, next) {
    updateSuggestion(id, { status: next });
    setPulse((n) => n + 1);
  }

  return (
    <AppLayout variant="admin">
      <div className="page-header">
        <div>
          <h1>Suggestions</h1>
          <p className="page-lead">
            Inbox from every desk. Filter, note, and export — this is development context, not a
            second messaging product.
          </p>
        </div>
      </div>

      <section className="desk-panel card admin-breakdown">
        <h2>Breakdown</h2>
        <p className="muted">{breakdown.total} suggestion{breakdown.total === 1 ? "" : "s"} recorded.</p>
        <div className="admin-breakdown__grid">
          {[
            ["By role", breakdown.byRole],
            ["By category", breakdown.byCategory],
            ["By status", breakdown.byStatus],
          ].map(([title, items]) => (
            <div key={title}>
              <h3>{title}</h3>
              <ul>
                {items.map((item) => (
                  <li key={item.id}>
                    <span>{item.label}</span>
                    <span>
                      {item.count} · {item.pct}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="desk-panel card">
        <div className="admin-toolbar">
          <label className="field admin-toolbar__field">
            <span className="field-label">Role</span>
            <select className="field-select" value={role} onChange={(event) => setRole(event.target.value)}>
              {ROLES.map((item) => (
                <option key={item || "all"} value={item}>
                  {item ? toTitleCase(item) : "All roles"}
                </option>
              ))}
            </select>
          </label>
          <label className="field admin-toolbar__field">
            <span className="field-label">Category</span>
            <select className="field-select" value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="">All categories</option>
              {SUGGESTION_CATEGORIES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field admin-toolbar__field">
            <span className="field-label">Status</span>
            <select className="field-select" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All statuses</option>
              {SUGGESTION_STATUSES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <div className="admin-toolbar__actions">
            <button type="button" className="ghost-btn" onClick={() => download(exportSuggestions(rows, "csv"))}>
              Export CSV
            </button>
            <button type="button" className="ghost-btn" onClick={() => download(exportSuggestions(rows, "json"))}>
              Export JSON
            </button>
          </div>
        </div>

        <ul className="admin-suggest-list">
          {rows.map((item) => (
            <li key={item.id} className="card admin-suggest-item">
              <p className="muted">
                {item.from_name}
                {item.from_role ? ` · ${toTitleCase(item.from_role)}` : ""} ·{" "}
                {SUGGESTION_CATEGORIES.find((cat) => cat.id === item.category)?.label || "Other"} ·{" "}
                {formatDate(item.created_at)}
              </p>
              <p>{item.text}</p>
              <label className="field">
                <span className="field-label">Status</span>
                <select
                  className="field-select"
                  value={item.status || "new"}
                  onChange={(event) => setStatusFor(item.id, event.target.value)}
                >
                  {SUGGESTION_STATUSES.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field-label">Admin notes</span>
                <textarea
                  className="field-textarea"
                  rows={3}
                  value={notesDraft[item.id] ?? item.admin_notes ?? ""}
                  onChange={(event) =>
                    setNotesDraft((current) => ({ ...current, [item.id]: event.target.value }))
                  }
                />
              </label>
              <button type="button" className="primary-btn" onClick={() => saveNotes(item.id)}>
                Save notes
              </button>
            </li>
          ))}
          {!rows.length ? <li className="muted">No suggestions match these filters.</li> : null}
        </ul>
      </section>
    </AppLayout>
  );
}
